import { useQuery } from '@tanstack/react-query';

import { agentsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

interface TranslationLocaleApi {
  code?: string | undefined;
  display_name?: string | undefined;
  is_translation?: boolean | undefined;
}

export interface TranslationLocale {
  /** BCP-47 code sent verbatim in `targetLocales`, e.g. `es-419`. */
  code: string;
  displayName: string;
  /** False for en-GB: UK spelling on the same English, not a translation. */
  isTranslation: boolean;
}

/**
 * Locales google-agent can translate enriched GMC content into. Baked into the deployed image,
 * so it cannot change within a session. `enabled` off for users who cannot pick one.
 */
export function useTranslationLocales(enabled = true) {
  return useQuery<TranslationLocale[]>({
    queryKey: ['configuration', 'translation-locales'],
    staleTime: Infinity,
    enabled,
    queryFn: async () => {
      const rows = await agentsClient
        .get(endpoints.agents.googleTranslationLocales())
        .json<TranslationLocaleApi[]>();
      return rows
        .filter((r): r is TranslationLocaleApi & { code: string } => Boolean(r.code))
        .map((r) => ({
          code: r.code,
          displayName: r.display_name ?? r.code,
          isTranslation: r.is_translation ?? true,
        }));
    },
  });
}

/**
 * A locale as a person reads it: `UK English (en-GB)`, name first and the code in brackets.
 * `en-US` is the enriched source and is not in google-agent's translation list, so it is named
 * here; a code the list does not know (or before it loads) shows as the bare code.
 */
export function formatLocale(code: string, locales: readonly TranslationLocale[] | undefined) {
  if (code.toLowerCase() === 'en-us') return `English (${code})`;
  const name = locales?.find((l) => l.code.toLowerCase() === code.toLowerCase())?.displayName;
  if (!name || name === code) return code;
  // "Spanish (Spain)" already has a bracket: "Spanish (Spain, es-ES)", not "(Spain) (es-ES)".
  return name.endsWith(')') ? `${name.slice(0, -1)}, ${code})` : `${name} (${code})`;
}
